import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import {throwError as observableThrowError, lastValueFrom } from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIArrayResponse, GenericAPIResponse, LoginApiResponse } from '../models/api-response';
import { CameraCommand, GameVideoFileGroup, QwikcutCamera, QwikcutCameraCurrentRecordingData, QwikcutCameraRelease, QwikcutCameraReleaseArtifactKind, QwikcutCameraReleaseChannel, QwikcutCameraReleaseType, QwikcutCameraUpdateEvent, QwikcutCameraSoftware, QwikcutCameraSoftwareType, QwikcutCameraZrokSsh, RoiConfig, RoiPoint, ScoreboardData, TrackingMode } from '../models/qwikcut-camera';
import { Sport } from '../models/conference';
import { Game } from '../models/game';
import { Team } from '../models/team';

@Injectable({
  providedIn: 'root'
})
export class QwikcutCameraService {

  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelperService,
    private router: Router
  ) { }

  getCameraApiUrl(cameraId: string): string {
    return `https://${cameraId}.zrok.qwikcut.com`;
  }

  registerCameraToTeam(teamid: string, cameraId: string, label?: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, cameraId, label};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  removeCameraFromTeam(teamid: string, cameraId: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, cameraId};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createCamera(data: Partial<QwikcutCamera>): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {data};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateCamera(cameraId: string, update: Partial<QwikcutCamera>): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {update};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCamera>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  sendCameraCommandSocketio(cameraId: string, command: CameraCommand, data?: any): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {command, data};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getTeamCameras(teamid: string): Promise<GenericAPIArrayResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/team/${teamid}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAllCameras(): Promise<GenericAPIArrayResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCamera(cameraId: string): Promise<GenericAPIResponse<QwikcutCamera>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCamera>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraZrokSsh(cameraId: string): Promise<GenericAPIResponse<QwikcutCameraZrokSsh>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/${cameraId}/zrok-ssh`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraZrokSsh>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createQwikcutCameraSoftware(name: string, type: QwikcutCameraSoftwareType, sportid?: Sport): Promise<GenericAPIResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software`;
    const headers = this.apiHelper.apiHeaderWithToken();
		const body = {name, type, sportid};

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraSoftware>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  incrementQwikcutCameraSoftwareVersion(_id: string): Promise<GenericAPIResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCameraSoftware>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteQwikcutCameraSoftwareVersion(_id: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getAllQwikcutCameraSoftware(): Promise<GenericAPIArrayResponse<QwikcutCameraSoftware>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/software`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraSoftware>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getQwikcutCameraReleases(): Promise<GenericAPIArrayResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraRelease>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  createQwikcutCameraReleaseUploadUrls(version: string, files: {filename: string, contentType?: string, kind?: QwikcutCameraReleaseArtifactKind}[], options?: {releaseType?: QwikcutCameraReleaseType, sportid?: string}): Promise<GenericAPIResponse<{bucket: string, prefix: string, urls: {filename: string, key: string, url: string}[]}>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/upload-url`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {version, files, ...(options || {})};

    return lastValueFrom(this.http.post<GenericAPIResponse<{bucket: string, prefix: string, urls: {filename: string, key: string, url: string}[]}>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  finalizeQwikcutCameraRelease(data: {version: string, releaseType?: QwikcutCameraReleaseType, sportid?: string, files?: {filename: string, contentType?: string, kind?: QwikcutCameraReleaseArtifactKind}[], channel?: QwikcutCameraReleaseChannel, notes?: string, targets?: {cameraIds?: string[], excludeCameraIds?: string[]}}): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/finalize`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, data, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateQwikcutCameraRelease(_id: string, update: Partial<QwikcutCameraRelease>): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {update};

    return lastValueFrom(this.http.put<GenericAPIResponse<QwikcutCameraRelease>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  publishQwikcutCameraRelease(_id: string): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}/publish`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  disableQwikcutCameraRelease(_id: string): Promise<GenericAPIResponse<QwikcutCameraRelease>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${_id}/disable`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<QwikcutCameraRelease>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getQwikcutCameraReleaseUpdateEvents(releaseId: string): Promise<GenericAPIArrayResponse<QwikcutCameraUpdateEvent>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/releases/${releaseId}/update-events`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraUpdateEvent>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  //directly communicate with the camera

  shutdownCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/shutdown`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  rebootCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/reboot`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  rebootAxisCamera(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/reboot-axis-camera`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  restartCameraSoftware(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/restart`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraCurrentRecording(cameraId: string): Promise<GenericAPIResponse<QwikcutCameraCurrentRecordingData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/current-recording`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<QwikcutCameraCurrentRecordingData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraCurrentRecordings(cameraId: string): Promise<GenericAPIArrayResponse<Game>> {
    const url = `${this.getCameraApiUrl(cameraId)}/current-recordings`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<Game>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getUpcomingGamesForCamera(cameraId: string, teamid?: string): Promise<GenericAPIArrayResponse<Game>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/games/${cameraId}?extendedRange=true${teamid != null ? `&teamid=${teamid}` : ''}`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<Game>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCameraRecordingEnd(cameraId: string, recordingEnd: Date): Promise<GenericAPIArrayResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/set-recording-end`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {recordingEnd};

    return lastValueFrom(this.http.post<GenericAPIArrayResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getDeviceIpAddress(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/device-ip-address`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<string>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getDeviceTemps(cameraId: string): Promise<GenericAPIResponse<{cpu: number, gpu: number}>> {
    const url = `${this.getCameraApiUrl(cameraId)}/device-temps`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<{cpu: number, gpu: number}>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCameraLogs(cameraId: string, full: boolean = false): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/${full ? 'full-' : ''}logs`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  startCalibrationProcess(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { };

    return lastValueFrom(this.http.post<GenericAPIResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCalibrationMode(cameraId: string, mode: 'stitch' | 'align'): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration-mode`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { mode };

    return lastValueFrom(this.http.post<GenericAPIResponse<string>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  stopCalibrationProcess(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.put<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  recomputeCameraHomography(cameraId: string, nfeatures: number = 10240): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/recompute-homography`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = { nfeatures };

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resetCameraHomography(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/reset`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setRoi(cameraId: string, roiConfig: RoiConfig): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/set-roi`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {roiConfig};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setCropOffset(cameraId: string, type: 'top' | 'bottom', cropOffset: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/crop-offset`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, cropOffset};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setRotation(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/rotation`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setXShift(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/xshift`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setYShift(cameraId: string, type: 'left' | 'right', value: number): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/yshift`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {type, value};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  uploadProbedImages(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/calibration/upload-probed-images`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  captureRawFeeds(cameraId: string, gameid: string, recordTime?: number): Promise<GenericAPIResponse<undefined>> {
    const body = {gameid, recordTime};
    const url = `${this.getCameraApiUrl(cameraId)}/capture-raw-feeds`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  stopAllCaptures(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/stop-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, {}, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  startGameCapture(cameraId: string, gameid: string): Promise<GenericAPIResponse<undefined>> {
    const body = {gameid};
    const url = `${this.getCameraApiUrl(cameraId)}/start-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  restartGameCapture(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/restart-capture`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setTrackingMode(cameraId: string, mode: TrackingMode): Promise<GenericAPIResponse<undefined>> {
    const body = {mode};
    const url = `${this.getCameraApiUrl(cameraId)}/tracking-mode`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  checkForUpdates(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/check-for-updates`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<undefined>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getCurrentSoftware(cameraId: string): Promise<GenericAPIArrayResponse<QwikcutCameraSoftware>> {
    const url = `${this.getCameraApiUrl(cameraId)}/software`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<QwikcutCameraSoftware>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getOptics(cameraId: string): Promise<GenericAPIResponse<{zoom: number, focus: number}>> {
    const url = `${this.getCameraApiUrl(cameraId)}/optics`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<{zoom: number, focus: number}>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setZoom(cameraId: string, zoom: number): Promise<GenericAPIResponse<undefined>> {
    const body = {zoom};
    const url = `${this.getCameraApiUrl(cameraId)}/zoom`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  setFocus(cameraId: string, focus: number): Promise<GenericAPIResponse<undefined>> {
    const body = {focus};
    const url = `${this.getCameraApiUrl(cameraId)}/focus`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  performAutofocus(cameraId: string): Promise<GenericAPIResponse<undefined>> {
    const body = {};
    const url = `${this.getCameraApiUrl(cameraId)}/autofocus`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getRebootReason(cameraId: string): Promise<GenericAPIResponse<string>> {
    const url = `${this.getCameraApiUrl(cameraId)}/reboot-reason`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<string>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getExistingGameFiles(cameraId: string): Promise<GenericAPIArrayResponse<GameVideoFileGroup>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIArrayResponse<GameVideoFileGroup>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  uploadExistingGameFiles(cameraId: string, gameid: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files/upload`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {gameid};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  deleteExistingGameFile(cameraId: string, filePath: string): Promise<GenericAPIResponse<undefined>> {
    const url = `${this.getCameraApiUrl(cameraId)}/game-files/delete`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {filePath};

    return lastValueFrom(this.http.post<GenericAPIResponse<undefined>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  getScoreboardData(cameraId: string): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.get<GenericAPIResponse<ScoreboardData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  updateScoreboard(cameraId: string, body: ScoreboardData): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.post<GenericAPIResponse<ScoreboardData>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  resetScoreboard(cameraId: string): Promise<GenericAPIResponse<ScoreboardData>> {
    const url = `${this.getCameraApiUrl(cameraId)}/scoreboard`;
    const headers = this.apiHelper.apiHeaderWithToken();

    return lastValueFrom(this.http.delete<GenericAPIResponse<ScoreboardData>>(url, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  registerYoutubeAccountForTeam(teamid: string, accessCode: string, redirectUri: string): Promise<GenericAPIResponse<Team>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/register-youtube`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid, accessCode, redirectUri};

    return lastValueFrom(this.http.post<GenericAPIResponse<Team>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

  unregisterYoutubeAccountForTeam(teamid: string): Promise<GenericAPIResponse<Team>> {
    const url = `${this.apiHelper.apiUrl()}/qwikcutcamera/unregister-youtube`;
    const headers = this.apiHelper.apiHeaderWithToken();
    const body = {teamid};

    return lastValueFrom(this.http.post<GenericAPIResponse<Team>>(url, body, { headers }).pipe(
      map(res => res),
      catchError(err => {
        console.log(err);
        return observableThrowError(err.message);
      }),));
  }

}
